/**
 * Battle Cards — what a card costs, when it unlocks, and what a day's play pays.
 *
 * **These numbers were `PROPOSED_ECONOMY` in `shared/tools/card-gauntlet.ts`.** They lived in
 * the harness while they were a proposal; they ship now, so they live here and the harness reads
 * them, because a shop that charges one price and a simulator that measures another is a
 * simulator measuring a game nobody plays.
 *
 * Every value below is a tuning-loop output with a criterion attached — see
 * `docs/BATTLE_CARDS_TUNING_LOG.md`. Do not adjust one without re-running the gauntlet: they are
 * balanced against each other, not chosen individually.
 *
 * Pure data. No I/O, no RNG.
 */

/**
 * **SKILLS COST SUIKA, AND ONLY SUIKA (Beta 21.52).** There was a second skill currency, Scrolls
 * (`soul_shards` in code), paid by campaign wins. The owner removed it (2026-10-26): Suika is the
 * game's one currency, and the campaign pays it. The economy below is still COUNTED in the unit the
 * Scrolls were, a **skill drop** (one eighth of a copy), because every Game Health criterion that
 * tunes it is a ratio of copies to clears; a drop is paid to the player as
 * {@link SUIKA_PER_SKILL_DROP} Suika, so the campaign buys skills exactly as fast as it did.
 * The player never sees the word.
 */

/**
 * The skill drops a soul node's FIRST clear pays ("The Last Souls"), as Suika: one copy's
 * worth over {@link CARD_COPY_CLEARS}.
 */
export const SOUL_FIRST_CLEAR_DROPS = 5;

/** The skill drops a world's Soul Token gift pays on top of its Soul Dust, as Suika. */
export const GIFT_SKILL_DROPS = 3;

/**
 * What a Common printing costs, in skill drops: one soul-node first clear.
 *
 * Anchored to the grant rather than picked so the shop's entry price is always exactly "one
 * soul freed", which is the sentence the campaign already teaches the player.
 */
export const COMMON_CARD_PRICE = SOUL_FIRST_CLEAR_DROPS;

/**
 * **WHAT ONE COPY COSTS, AS A MULTIPLE OF WHAT A CLEAR PAYS. The economy's one dial.**
 *
 * A copy used to cost exactly ONE first clear, anchored so the shop's entry price was always
 * "one soul freed" — the sentence the campaign already teaches. It is a lovely line, and it made
 * the shop free. A campaign has far more soul nodes than a loadout has copies, so **every clear
 * bought a copy and the player finished with more than four times what they could spend**:
 * criterion G7 (do shards still mean something?) read **4.32x earned against a 3.00x ceiling**.
 * At that ratio a purchase is not a decision, it is a formality on the way to owning everything.
 *
 * **1.6 — a copy costs MORE than a clear pays and less than two.** That is the whole design
 * statement: you cannot buy a card with every clear, so which card is a question again. Measured
 * on `--seeds 1337,42`:
 *
 * | clears per copy | full loadout | G7 (<=3.00x) | G6 (>=1.00x) | G10 every card sells | G8 first buy |
 * |---|---|---|---|---|---|
 * | 1.0 (the anchor) | 40 | **4.32x ✗** | 4.32x ✓ | 1.00x ✓ | W1 ✓ |
 * | **1.6 (this)** | **64** | **2.70x ✓** | **2.70x ✓** | **1.00x ✓** | **W1 ✓** |
 * | 2.0 | 80 | 2.16x ✓ | 2.16x ✓ | **0.88x ✗** | W1 ✓ |
 *
 * **Two clears is where it breaks**, and G10 is what breaks: at 80 a full loadout, 12% of the
 * catalogue never sells to anyone under any buying policy — a shop with shelves nobody reaches
 * is not a tighter economy, it is a smaller one. 1.6 is the whole of G7's improvement with none
 * of that.
 *
 * **The ladder stays FLAT.** Every copy costs the same — the first, which makes a card Common,
 * and the fourth, which makes it Epic. A rising price would stall the ladder exactly where it is
 * meant to pay off.
 */
export const CARD_COPY_CLEARS = 1.6;

/**
 * What one COPY costs, in skill drops. Derived, never typed: {@link CARD_COPY_CLEARS} is where
 * the decision lives, and the price has to move with the grant if the grant is ever re-tuned.
 */
export const CARD_COPY_PRICE = Math.round(COMMON_CARD_PRICE * CARD_COPY_CLEARS);

/**
 * **What one copy costs: the ONE skill price the player sees, in Suika.**
 *
 * Suika comes from the campaign (every payout below, as {@link SUIKA_PER_SKILL_DROP}) and from
 * CARE (training, the daily login, an egg of a species already owned), and both roads end at
 * this one shelf. Steep next to a campaign battle's 1 Suika entry, on purpose.
 *
 * **1000, halved from 2000** when it was the care road's price beside a Scroll one; kept at
 * 1000 when Scrolls went, so the care road did not move either.
 *
 * **WHAT MEASURES IT, AND WHAT DOES NOT.** The gauntlet counts the campaign's income in skill
 * drops, and a drop is defined as a share of THIS price, so G2/G6/G7 measure the campaign road at
 * any price. The care road's Suika is not in the model: a player who trains every day collects
 * faster than G6/G7 say. That blind spot predates the change (the care road always bought from
 * this shelf); the free refill no longer feeds it (`SUIKA_FREE_REFILL_TARGET`).
 */
export const CARD_COPY_SUIKA_PRICE = 1000;

/**
 * **THE SUIKA ONE SKILL DROP PAYS — an eighth of a copy, 125.** Derived from the two prices, never
 * typed: what one Scroll was worth when a copy cost {@link CARD_COPY_PRICE} Scrolls or
 * {@link CARD_COPY_SUIKA_PRICE} Suika (owner, 2026-10-26: "same speed").
 */
export const SUIKA_PER_SKILL_DROP = CARD_COPY_SUIKA_PRICE / CARD_COPY_PRICE;

/** A payout counted in skill drops, as the Suika the player is paid. */
export function skillDropsAsSuika(drops: number): number {
  return Math.round(drops * SUIKA_PER_SKILL_DROP);
}

/** What a soul node's first clear pays, in Suika (625). */
export const SOUL_FIRST_CLEAR_SUIKA = skillDropsAsSuika(SOUL_FIRST_CLEAR_DROPS);

/** What a Soul Token gift pays on top of its Soul Dust, in Suika (375). */
export const GIFT_SKILL_SUIKA = skillDropsAsSuika(GIFT_SKILL_DROPS);

/**
 * Kept for the shelf's rung labels only — what a Rare printing used to cost: FOUR first
 * clears, which is exactly what four copies at {@link CARD_COPY_PRICE} now costs.
 *
 * Halving it was tried and reverted. G2 (does buying help?) did not move — +2.6% → −0.9%, both
 * indistinguishable from zero — while solvency ran from 1.18x of a full loadout to 2.44x, most
 * of the way to G7's ceiling where the shop has stopped asking anything. The price is not what
 * stops buying from helping, and four clears is what keeps a shard worth having.
 */
export const RARE_CARD_PRICE = SOUL_FIRST_CLEAR_DROPS * 4;

/**
 * Pet level at which Rare printings appear on the shelf. **15, measured up from 10.**
 *
 * The campaign's level bands put world N over levels `[1+10N … 10+10N]` and the player meets each
 * node at parity, so a level-10 gate opened the Rare shelf inside world 1, on top of the first
 * purchase the player had just made. G9 wants that second, bigger decision in worlds 2-4, after
 * Commons have been understood. 15 lands it in world 2 while staying far above the free tier's
 * level ceiling — so signing up is still what opens it.
 */
export const RARE_CARD_UNLOCK_LEVEL = 15;

/**
 * **THE DAILY LADDER: what the Nth win of a day pays.**
 *
 * Read as "wins up to `throughWins` pay `drops`" (skill drops, paid as Suika), walked in order, falling through to
 * {@link DAILY_WIN_DROP_TAIL}. The shape is Clash Royale's daily track — the first few wins pay
 * big, the next several pay less, and **every win after that still pays.**
 *
 * The never-zero tail is not a generosity knob, it is the thing that makes the economy survive a
 * collection game. A flat capped drop collapses by two thirds the moment a player owns a second
 * pet and keeps collapsing; the ladder takes a one-time 18% haircut at two pets and then flattens
 * completely, so a fifth pet costs nothing at all. G14 measures the principle directly.
 */
export const DAILY_WIN_DROP_TIERS: readonly { throughWins: number; drops: number }[] = [
  { throughWins: 3, drops: 2 },
  { throughWins: 10, drops: 1 },
];

/** What every win beyond the last rung pays. **Must stay > 0, or the ladder is a cap again.** */
export const DAILY_WIN_DROP_TAIL = 1;

/**
 * What the next win pays, given how many the player has already banked today.
 *
 * The ladder is stated once as data and walked here, so a rung can be re-priced or added without
 * touching a chain of comparisons.
 */
export function dailyWinDrop(winsAlreadyToday: number): number {
  for (const tier of DAILY_WIN_DROP_TIERS) {
    if (winsAlreadyToday < tier.throughWins) return tier.drops;
  }
  return DAILY_WIN_DROP_TAIL;
}

/**
 * The ladder flattened into two parallel arrays plus the tail, for the one caller that cannot
 * walk it itself: `grant_daily_win_drop`.
 *
 * The rung depends on how many wins the day has banked, and only the database knows that — it is
 * the thing incrementing the counter — so the ladder has to travel with the call. Derived from
 * {@link DAILY_WIN_DROP_TIERS} rather than restated, so the SQL and {@link dailyWinDrop} can never
 * disagree about what the third win of a day is worth.
 */
export const DAILY_WIN_DROP_RPC_ARGS = {
  throughWins: DAILY_WIN_DROP_TIERS.map((tier) => tier.throughWins),
  rungShards: DAILY_WIN_DROP_TIERS.map((tier) => tier.drops),
  tail: DAILY_WIN_DROP_TAIL,
} as const;
